// shared-lib/cabinet-runs/resolvePolicy.js
//
// The ONE place a re-solve's deliberate differences are written down
// (proposal §7). A SITE is who is calling — the server's scan step 10b or a
// web edit — and the effective POLICY for a run is a function of the site and
// the run's provenance. Everything the two materializers used to decide with
// a scattered `if (run.source === …)` is a field here; `materializeRun`,
// `mergeResolvedRuns` and `incumbents` never read `run.source` themselves.
'use strict';

const { uppersEnabled } = require('./profiles');
const { isAutoRun, isDerivedRun } = require('./runSource');

const RESOLVE_SITES = Object.freeze({ EDIT: 'edit', SCAN: 'scan' });

/**
 * What happens to the run's UPPER tier on this solve.
 *   'rebuild' — the fill emits uppers from the lower bays; the run's owned
 *               uppers in the room are swept and replaced.
 *   'keep'    — the fill emits none; the run's owned uppers stay as they are
 *               (measured by the scan, or built by an earlier solve).
 *   'none'    — the fill emits none; the run's owned uppers are swept.
 *
 * Ruling B (proposal §11, user 2026-09-05): a scanned run's uppers stay
 * measured on the first web edit; the run's uppers toggle (`uppers: true`) is
 * the explicit way to rebuild them. The server never rebuilds uppers.
 */
function uppersMode(run, site) {
  // Every exported entry point validates its own site: an unknown one throws
  // rather than defaulting (global constraint #6). Without this an argument
  // slip — `uppersMode(site, run)` — silently answers for the edit site.
  if (site !== RESOLVE_SITES.EDIT && site !== RESOLVE_SITES.SCAN) {
    throw new Error(`uppersMode: unknown site ${String(site)}`);
  }
  if (site === RESOLVE_SITES.SCAN) return 'keep';
  if (run?.uppers === false) return 'none';
  if (run?.uppers === true) return 'rebuild';
  if (run?.source === 'scan') return 'keep';
  // Unset: the run's dimension PROFILE decides. A kitchen run rebuilds (what
  // this always did); a vanity run emits none, so the policy must agree with
  // the fill rather than promising uppers it will not build.
  return uppersEnabled(run) ? 'rebuild' : 'none';
}

/**
 * @param {'edit'|'scan'} site
 * @param {Object} run stored or hydrated run (`source`, `uppers` are read)
 * @returns {Readonly<{site, moveAppliances, uppers, keepMeasuredBodies, rebuildInstall, emitAppliances}>}
 */
function policyFor(site, run) {
  if (site !== RESOLVE_SITES.EDIT && site !== RESOLVE_SITES.SCAN) {
    throw new Error(`policyFor: unknown site ${String(site)}`);
  }
  // A run is not optional, and `{}` is not a safe stand-in for a missing one:
  // absent `source` IS the hand-drawn policy (uppers 'rebuild',
  // `emitAppliances` true), so a caller that lost its run would sweep a scan
  // run's measured uppers and double its appliances rather than fail (ledger
  // 57, global constraint #6 — no silent fallbacks).
  if (!run || typeof run !== 'object') {
    throw new Error('policyFor: a run is required');
  }
  const scan = site === RESOLVE_SITES.SCAN;
  const source = run?.source;
  return Object.freeze({
    site,
    // The run takes over its appliances' positions on an edit
    // (layout-relationships §2.5: "the first edit is the opt-in"). The server
    // never moves a measured appliance — `slideSinksToBays` is the scan site's
    // own bounded exception and runs before the merge.
    moveAppliances: !scan,
    uppers: uppersMode(run, site),
    // A scan run's bodies were MEASURED: they set the build depth, the
    // adjacent-wall opening reservation yields to them, tall fillers clear the
    // kept uppers, and the corner plan never assumes it can slide an
    // appliance. A property of the run, not the site — the web re-solve of a
    // scan run reproduces the server's geometry (RP3).
    keepMeasuredBodies: source === 'scan',
    // A generated run rebuilds its appliance install (surround, over-fridge
    // cabinet, hood) from the bay footprint (#1765); a scan run's install is
    // measured and stays; a hand-drawn run emits its own through
    // `buildApplianceObjects` (see `emitAppliances`). Never on the scan site.
    rebuildInstall: !scan && isAutoRun(run),
    // Only a hand-drawn run emits appliance OBJECTS; a derived run's appliances
    // belong to whoever emitted them and are positioned, never re-emitted
    // (proposal §4.4 row 2, RP2).
    emitAppliances: !scan && !isDerivedRun(run),
  });
}

module.exports = { RESOLVE_SITES, policyFor, uppersMode };
